'use client';

import type { ForwardedRef, PropsWithChildren, ReactElement } from 'react';
import React, { useRef } from 'react';
import { mergeProps } from 'react-aria';
import { Spinner } from 'components/Spinner';
import { useToggleButtonContext } from 'components/ToggleButtonProvider';
import styled, { css } from 'styled-components';
import { mergeRefs } from '@react-aria/utils';
import type { IconProps } from '@frameio/vapor-icons';
import { globalThemeValues, theme } from '@frameio/vapor-theme';
import type { AddDollarPrefix } from '../../utils/types';
import { textStyles } from '../Text/text-variants';
import { ButtonIcon } from './components/ButtonIcon';
import { isValidButtonProp } from './components/is-valid-button-prop';
import { disablePendingProps } from './isPending';
import type { UseButtonProps } from './useButton';
import { useButton } from './useButton';

/**
 * Sizes
 */

/**
 * @deprecated Support for absolute pixel values for `size` props will eventually
 * be removed as we look to eventually support responsive sizes.
 */

export const ICON_GAP = 4;

export const buttonPaddings = {
  small: {
    x: globalThemeValues.spaces[8],
    y: 0,
  },
  regular: {
    x: globalThemeValues.spaces[12],
    y: 0,
  },
} as const;

export const buttonBoxSizes = {
  small: 24,
  regular: 32,
} as const;

export const sizes = {
  small: css`
    ${textStyles.linkSmall};
    height: ${buttonBoxSizes.small}px;
    line-height: ${buttonBoxSizes.small}px;
    padding: ${buttonPaddings.small.y} ${buttonPaddings.small.x};
  `,
  regular: css`
    ${textStyles.link};
    height: ${buttonBoxSizes.regular}px;
    line-height: ${buttonBoxSizes.regular}px;
    padding: ${buttonPaddings.regular.y} ${buttonPaddings.regular.x};
  `,
} as const;

export type ButtonSize = keyof typeof sizes;

const TEXT = {
  isLoading: 'Is Loading...',
};

/**
 * Variants
 */

export const buttonVariants = {
  primary: () => {
    const tokens = theme.colors.components.button.primary;

    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      &[data-hovered] {
        background: ${tokens.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tokens.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${tokens.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;
      }
    `;
  },
  secondary: () => {
    const tokens = theme.colors.components.button.secondary;

    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      &[data-hovered] {
        background: ${tokens.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tokens.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${tokens.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;
      }
    `;
  },
  tertiary: () => {
    const secondary = theme.colors.components.button.secondary;
    return css`
      background: transparent;
      color: ${secondary.text};
      &[data-hovered] {
        background: ${theme.colors.components.button.tertiary.interactive
          .background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${secondary.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: transparent;
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;
      }
    `;
  },
  secure: () => {
    const secure = theme.colors.components.button.secure;
    return css`
      background: ${secure.background};
      color: ${secure.text};
      &[data-hovered] {
        background: ${secure.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${secure.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${secure.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;
      }
    `;
  },
  destructive: () => {
    const tokens = theme.colors.components.button.destructive;

    return css`
      background: ${tokens.background};
      color: ${tokens.text};
      &[data-hovered] {
        background: ${tokens.interactive.background.hover};
      }

      &[aria-expanded='true'],
      &[data-pressed] {
        background: ${tokens.interactive.background.pressed};
      }

      &:disabled,
      &[aria-disabled='true'] {
        background: ${tokens.interactive.background.disabled};
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;
      }
    `;
  },
  toggle: () => {
    const secondary = theme.colors.components.button.secondary;
    const selectedColor = theme.colors.components.link.interactive.text.active;
    const disabledColor = theme.colors.text.interactive.disabled;

    return css`
      background: transparent;
      color: ${secondary.text};

      &:disabled,
      &[aria-disabled='true'] {
        background: transparent;
        color: ${theme.colors.text.interactive.disabled};
        cursor: not-allowed;
      }

      &[data-hovered],
      &[data-pressed],
      &[aria-expanded='true'] {
        background: ${secondary.interactive.background.hover};
      }

      &[data-selected] {
        color: var(--selected-color, ${selectedColor});

        &:disabled,
        &[aria-disabled='true'] {
          background: transparent;
          color: var(--selected-color-disabled, ${disabledColor});
        }
      }
    `;
  },
} as const;

/**
 * Shapes
 */

const buttonShapes = {
  rectangular: css`
    border-radius: ${theme.radii[6]};
  `,
  rounded: css`
    border-radius: 20px;
  `,
};

export type ButtonShapes = keyof typeof buttonShapes;

export type SharedButtonProps = PropsWithChildren<UseButtonProps> &
  Pick<React.HTMLAttributes<HTMLButtonElement>, 'tabIndex'>;

/**
 * Button Component
 */

type ButtonVariants =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'secure'
  | 'destructive';

export type ButtonProps = SharedButtonProps & {
  /** Render button as a full-width element */
  block?: boolean;
  /** Render an icon from '@frameio/vapor-icons' before children */
  iconAfter?: ReactElement<IconProps>;
  /** Render an icon from '@frameio/vapor-icons' after children */
  iconBefore?: ReactElement<IconProps>;
  /** Render a spinner to represent a loading state */
  isLoading?: boolean;
  /** Minimum width of the button */
  minWidth?: string;
  /** Maximum width of the button */
  maxWidth?: string;
  /** Alter the size of the Button. */
  size?: ButtonSize;
  /** Alter the shape of the Button. default: 'rectangular' */
  shape?: ButtonShapes;
  /** Controls the style and color of buttons */
  variant?: ButtonVariants;
  /** Whether to space the text and icons evenly */
  spaceBetween?: boolean;
};

function Button(
  props: ButtonProps,
  forwardedRef: ForwardedRef<HTMLButtonElement>
) {
  const ref = useRef(null);

  const {
    size = 'regular',
    variant: variantProp = 'secondary',
    shape = 'rectangular',
    block = false,
    iconBefore,
    iconAfter,
    isLoading = false,
    minWidth = '0px',
    maxWidth = '100%',
    children,
    elementType,
    spaceBetween = false,
    ...other
  } = disablePendingProps(props);

  const { isToggle } = useToggleButtonContext();

  const variant = isToggle ? 'toggle' : variantProp;

  const { buttonProps, hasSharedHover } = useButton(ref, {
    ...other,
    enableSharedHover: variant === 'tertiary' || variant === 'toggle',
    elementType,
  });

  return (
    <StyledButton
      ref={mergeRefs(ref, forwardedRef)}
      $maxWidth={maxWidth}
      $minWidth={minWidth}
      $shape={shape}
      $size={size}
      $variant={variant}
      aria-disabled={isLoading || undefined}
      aria-label={isLoading ? TEXT.isLoading : undefined}
      as={elementType}
      data-block={block || undefined}
      data-loading={isLoading || undefined}
      data-shared-hover-enabled={hasSharedHover || undefined}
      data-space-between={spaceBetween || undefined}
      {...mergeProps(buttonProps, props)}
    >
      {isLoading ? (
        <Spinner
          aria-label={TEXT.isLoading}
          centered
          delay={0}
          isLoading={true}
          size="x-small"
          variant="brandOverlay"
        />
      ) : null}
      <>
        <ButtonIcon icon={iconBefore} size={size} />
        <ButtonText>{children}</ButtonText>
        <ButtonIcon icon={iconAfter} size={size} />
      </>
    </StyledButton>
  );
}

export const buttonReset = css`
  &[type='button'] {
    -webkit-appearance: none;
    appearance: none;
  }
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background: none;
  border: none;
  box-shadow: none;
  box-sizing: border-box;
  cursor: pointer;
  padding: 0;
  text-align: center;
  text-decoration: none;
  user-select: none;
  white-space: nowrap;
`;

export const buttonTransitions = css`
  transition-duration: 0.15s;
  transition-property: background, box-shadow, color;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
`;

type StyledButtonProps = AddDollarPrefix<
  Pick<ButtonProps, 'size' | 'block' | 'shape'>
> & {
  $isLoading: boolean;
  $variant: ButtonVariants | 'toggle';
  $sharedHoverEnabled: boolean;
  $minWidth: string;
  $maxWidth: string;
};

export const StyledButton = styled.button.withConfig({
  shouldForwardProp: isValidButtonProp,
})<StyledButtonProps>`
  ${buttonReset};
  ${buttonTransitions};
  font-family: ${theme.fonts.body};
  margin: 0;
  align-items: center;
  display: inline-flex;
  gap: ${theme.spaces[ICON_GAP]};
  justify-content: center;
  min-width: ${(p) => p.$minWidth};
  max-width: ${(p) => p.$maxWidth};
  outline: none;
  position: relative;
  vertical-align: middle;
  flex-shrink: 0;

  & > svg {
    flex-shrink: 0;
  }

  &[data-focus-visible] {
    box-shadow: 0 0 0 1.5px ${theme.colors.border.interactive.focused};
  }

  &[data-loading] {
    cursor: default;
  }

  &[data-loading] > *:not([role='progressbar']) {
    opacity: 0;
  }

  ${(p) => sizes[p.$size]};

  ${(p) => buttonVariants[p.$variant]};

  ${(p) => buttonShapes[p.$shape]};

  &[data-shared-hover-enabled] {
    background: transparent !important;
    will-change: transform;
  }

  &[data-block] {
    display: flex;
    width: 100%;
  }

  &[data-loading] {
    line-height: 0;
  }

  &[data-space-between] {
    justify-content: space-between;
  }
`;

const ButtonText = styled.span`
  display: flex;
  min-width: 0;
`;

/**
 * A Button component which wraps react-aria's useButton hook.
 *
 * @see WAI-ARIA https://www.w3.org/WAI/ARIA/apg/patterns/button/
 * @see Docs     http://vapor.frame.io/components/button
 * @see Docs     https://react-spectrum.adobe.com/react-aria/useButton.html
 * @example
 * <Button onPress={log('pressed!')}>
 *   Hello world
 * </Button>
 */

const _Button = React.forwardRef(Button);
_Button.displayName = 'Button';
export { _Button as Button };
